import React from 'react';
import type { IntlShape } from 'react-intl';

import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';

import { fetchMacroAttributes } from '../../editor-extensions/fetchMacroAttributes.ts';
import {
	BODIED_EXTENSION_TYPE,
	EXTENSION_TYPE,
	INLINE_EXTENSION_TYPE,
	MULTI_BODIED_EXTENSION_TYPE,
	REGULAR_EXTENSION_TYPE,
} from '../../extensionConstants.ts';
import type { ExtensionNodeType } from '../../extensions-common/extensionNodeTypes.ts';

import type { LegacyMacroManifest, LegacyParameter } from './extensionTypes.ts';
// eslint-disable-next-line import/no-cycle
import { getIconComponent, DeprecatedExtensionPlaceholder } from './MacroComponents.tsx';
import { transformParams } from './field-mappings/index.ts';
import { isValueSet } from './utils.ts';
import type { MacroAttributes } from '@atlaskit/editor-common/provider-factory/macro-provider';

export const safeGetMacroName = (macro: LegacyMacroManifest): string =>
	macro.alternateId || macro.macroName;

export const getExtensionBodyType = (macro: LegacyMacroManifest): ExtensionNodeType => {
	const { bodyType, outputType } = macro.formDetails.body;

	if (bodyType === 'MULTI_BODY_RICH_TEXT') {
		return MULTI_BODIED_EXTENSION_TYPE;
	}

	//        none  plain   rich
	// block   ext   ext    body
	// inline  in    in     body
	if (bodyType === 'RICH_TEXT') {
		return BODIED_EXTENSION_TYPE;
	}

	if (outputType === 'INLINE') {
		return INLINE_EXTENSION_TYPE;
	}

	return REGULAR_EXTENSION_TYPE;
};

export const buildIconObject = (macro: LegacyMacroManifest) => {
	return {
		'48': (): Promise<() => JSX.Element> => Promise.resolve(() => getIconComponent(macro)),
	};
};

export const getVisibleParameters = (macro: LegacyMacroManifest): LegacyParameter[] =>
	(macro.formDetails &&
		(macro.formDetails.parameters || []).filter(
			(parameter: LegacyParameter) => !parameter.hidden,
		)) ||
	[];

const hasParameter = (
	paramName: string,
	extension: ExtensionParams<{ macroParams?: object }>,
	macro: LegacyMacroManifest,
) => {
	if (extension && extension.parameters && extension.parameters.macroParams) {
		const params = transformParams('transformBefore', macro, {
			...extension.parameters.macroParams,
		});

		if (paramName in params) {
			let value = params[paramName];
			if (typeof value === 'object' && value !== null && value.hasOwnProperty('value')) {
				value = value.value;
			}
			return isValueSet(value);
		}
	}
	return false;
};

// Note: macro.formDetails.parameters are coming from the backend, not from fieldTransformers under field-mappings.
export const isMissingAnyRequiredParameters = (
	macro: LegacyMacroManifest,
	extension: ExtensionParams<object>,
): boolean =>
	macro.formDetails.parameters.some(
		(param) =>
			param.required &&
			param.defaultValue === null &&
			!param.hidden &&
			!hasParameter(param.name, extension, macro),
	);

export const prepareMacro = (
	macroDefinition: any,
	contentId: any,
	selectedMacro?: ADNode | null,
	intl?: IntlShape,
): Promise<MacroAttributes> => {
	return fetchMacroAttributes({
		macroDefinition: {
			name,
			body: '',
			params: {},
			...macroDefinition,
		},
		contentId,
		selectedMacro,
		intl,
	});
};

export const canRenderInTheEditor = (macro: LegacyMacroManifest): boolean => {
	switch (macro.macroName) {
		case 'pagetree': // pagetree is not spa friendly and will only work sometimes
			return false;

		default:
			return true;
	}
};

export const shouldUseMacroBrowser = (
	macro: LegacyMacroManifest,
	excluded: string[] = [],
): boolean => {
	// This is to opt-out a macro quickly from the new experience
	// using the list in `next/packages/editor-features/src/featureFlagsUtils.ts`
	if (excluded.includes(macro.macroName)) {
		return true;
	}

	// Plain text macros are not supported yet
	if (macro.formDetails.body.bodyType === 'PLAIN_TEXT') {
		return true;
	}

	return macro.customConfigEditor;
};

export const needsToForceConfig = (macro: LegacyMacroManifest): boolean => {
	// Need to force config for Plain text macros
	if (macro.formDetails.body.bodyType === 'PLAIN_TEXT') {
		return true;
	}

	switch (macro.macroName) {
		// CFE-2344: despite not having a required parameter, the children macro should open the macro browser
		// so that the user can select a parent page. Without this, on a brand new page, the children display
		// macro renders a loading state, then completely disappears and looks broken.
		case 'children': // Keep getting message saying "failed to retrieve..."
		// CONFDEV-62041: tasks report macro also doesn't have a required parameter, but without any parameters
		// it tries to search the entirety of Confluence for tasks and usually times out, so let's ask for parameters anyway.
		case 'tasks-report-macro': // Has issues.
			return true;
		default:
			return macro.customConfigEditor;
	}
};

export const shouldShowPlaceholder = (
	macro: LegacyMacroManifest,
	excludedMacros: string[] = [],
	extension: ExtensionParams<object>,
	isEditor: boolean,
): boolean =>
	// NOTE: Since it's not possible to insert macros without required fields using
	// Macro Browser so there's no point of showing placeholder for them. @see ED-12141
	!shouldUseMacroBrowser(macro, excludedMacros) &&
	// Maintaining the anchor's unique(?) status as a non-rendering-in-publish-view macro
	(isEditor || macro.macroName !== 'anchor') &&
	// Missing required parameters
	isMissingAnyRequiredParameters(macro, extension);

/**
 * Returns the extension manifest for a deprecated macro placeholder.
 * No quick insert module / node.update function is included to prevent insertion and
 * editing of existing deprecated macros.
 */
export const getDeprecatedManifest = (
	macro: LegacyMacroManifest,
): {
	type: string;
	key: string;
	title: string;
	description: string;
	icons: {
		'48': () => Promise<() => JSX.Element>;
	};
	modules: {
		nodes: {
			default: {
				type: ExtensionNodeType;
				preloadRender(): Promise<void>;
				renderSync(): () => React.JSX.Element;
				render(): Promise<() => React.JSX.Element>;
			};
		};
	};
} => {
	return {
		type: EXTENSION_TYPE.MACRO_CORE,
		key: macro.macroName,
		title: macro.title,
		description: macro.description,
		icons: buildIconObject(macro),
		modules: {
			nodes: {
				default: {
					type: 'extension' as ExtensionNodeType,
					async preloadRender(): Promise<void> {
						// Intentionally left empty. But the function needs to be defined
					},
					renderSync() {
						return (): React.JSX.Element => {
							const macroTitle = macro.title.replace(' (Deprecated)', '');
							return <DeprecatedExtensionPlaceholder macroName={macroTitle} />;
						};
					},
					async render(): Promise<() => React.JSX.Element> {
						return this.renderSync();
					},
				},
			},
		},
	};
};
